import { diffDateTime } from '@ocx/utils-date';
import { CardAccessoryExpirationBadge } from '@ocx/graphql';

/**
 * Decides whether the expiration badge should be rendered on an Offer Card,
 * driven by the resolved `cardExpirationBadge` config and the offer expiration date (OCST-29185).
 *
 * Rules:
 * - the badge is hidden when the config is explicitly disabled;
 * - the badge is hidden when the offer has no expiration date, regardless of config;
 * - a `thresholdHours` of `null` or `0` always shows the badge once there is an expiration date;
 * - a positive `thresholdHours` shows the badge only when the offer expires within that window
 *   (time until expiration `<=` threshold, compared at minute precision).
 *
 * When no config is provided the badge stays enabled, preserving the default behaviour.
 */
export const shouldShowOfferExpirationBadge = (
  config: CardAccessoryExpirationBadge | null | undefined,
  offerExpiresAt: string | null,
  now: Date = new Date(),
): boolean => {
  if (config && !config.enabled) {
    return false;
  }
  if (!offerExpiresAt) {
    return false;
  }

  const thresholdHours = config?.thresholdHours ?? null;
  // `null`/`0` threshold means "always show" once there is an expiration date.
  if (thresholdHours === null || thresholdHours === 0) {
    return true;
  }

  // Compare in minutes so a sub-hour overflow (e.g. 24h 1m vs a 24h threshold) is not
  // truncated away by date-fns' whole-hour difference and incorrectly counted as within range.
  const minutesUntilExpiration = diffDateTime(new Date(offerExpiresAt), now, 'minute', { roundingMethod: 'ceil' });

  return minutesUntilExpiration <= thresholdHours * 60;
};
